import type { DriveStep } from 'driver.js'

/**
 * Estilo del popover del tour. Cambia esta constante para alternar entre las
 * tres variantes tematizadas en index.css (A=minimal, B=cabecera, C=glass+badge).
 */
export const ESTILO_TOUR: 'A' | 'B' | 'C' = 'A'

/**
 * Tours de la guía (driver.js). Hay un tour GLOBAL (recorre el panel entero,
 * se ancla al menú y al header, que están en todas las rutas) y un tour POR
 * PANTALLA para las secciones con más chicha (Logística, Gestión, Grandes
 * Empresas, Agendamiento, Calendario, Conversaciones).
 *
 * Los pasos se anclan a atributos `data-tour="clave"` en el DOM (estables
 * frente a re-renders y cambios de clases). Si un elemento del paso no existe
 * en la pantalla actual, driver.js lo omite; aun así, cada tour por pantalla
 * solo se ofrece cuando su ruta está activa (ver mapaTours).
 */

export interface DefinicionTour {
  id: string
  titulo: string
  pasos: DriveStep[]
}

/** Tour general del panel: disponible en cualquier pantalla. */
export const TOUR_GLOBAL: DefinicionTour = {
  id: 'global',
  titulo: 'Recorrido general',
  pasos: [
    {
      element: '[data-tour="guia"]',
      popover: {
        title: 'Bienvenido a Táctica',
        description:
          'Esta guía te muestra las zonas clave del panel. Puedes abrirla cuando quieras desde este botón «Guía».',
      },
    },
    {
      element: '[data-tour="menu"]',
      popover: {
        title: 'Menú principal',
        description:
          'Tres bloques: Operación diaria, Clientes y Leads, y Sistema. Desde aquí navegas entre las pantallas.',
        side: 'right',
        align: 'start',
      },
    },
    {
      element: '[data-tour="nav-/logistica"]',
      popover: {
        title: 'Logística',
        description: 'Mapa y tabla de clientes por ruta. Clic en una fila abre el perfil 360º del cliente.',
        side: 'right',
      },
    },
    {
      element: '[data-tour="nav-/agendamiento"]',
      popover: {
        title: 'Agendamiento',
        description: 'Prepara y publica los albaranes del día en Holded. La pantalla principal es la lista.',
        side: 'right',
      },
    },
    {
      element: '[data-tour="nav-/calendario"]',
      popover: {
        title: 'Calendario',
        description: 'Visión mensual y semanal de los repartos ya agendados, con el detalle de cada día.',
        side: 'right',
      },
    },
    {
      element: '[data-tour="nav-/conversaciones"]',
      popover: {
        title: 'Conversaciones',
        description: 'La bandeja de WhatsApp: hablas con clientes y leads, y respondes dentro de la ventana de 24h.',
        side: 'right',
      },
    },
    {
      element: '[data-tour="nav-/gestion"]',
      popover: {
        title: 'Gestión',
        description:
          '«Esta semana» (a quién consultar) y «Seguimiento» (quién lleva sin pedir). El corazón del ciclo.',
        side: 'right',
      },
    },
    {
      element: '[data-tour="nav-/grandes-empresas"]',
      popover: {
        title: 'Grandes Empresas',
        description: 'Clientes GE: aquí configuras su recurrencia y el pedido fijo que se genera solo.',
        side: 'right',
      },
    },
    {
      element: '[data-tour="campana"]',
      popover: {
        title: 'Notificaciones',
        description: 'Avisos de incidencias, aplazamientos y albaranes automáticos de GE.',
        side: 'bottom',
        align: 'end',
      },
    },
    {
      popover: {
        title: '¡Listo!',
        description:
          'Eso es lo esencial. Cada pantalla tiene además su propia guía: pulsa «Guía» dentro de ella para verla.',
      },
    },
  ],
}

/** Tour específico de Logística. */
const TOUR_LOGISTICA: DefinicionTour = {
  id: 'logistica',
  titulo: 'Guía de Logística',
  pasos: [
    {
      element: '[data-tour="logistica-metricas"]',
      popover: {
        title: 'Métricas de carga',
        description:
          'Un vistazo rápido al estado del reparto: carga por ruta, cuántos clientes están geocodificados (salen en el mapa), cuántos llevan semanas sin pedir, y los que no tienen teléfono o ruta asignada. Son tu semáforo diario.',
        side: 'bottom',
      },
    },
    {
      element: '[data-tour="logistica-filtros"]',
      popover: {
        title: 'Buscar y filtrar',
        description:
          'Acota la lista por nombre, móvil o población, y filtra por semana (S1–S4) y por ruta. Útil para preparar la carga de un día concreto.',
        side: 'bottom',
      },
    },
    {
      element: '[data-tour="logistica-tabla"]',
      popover: {
        title: 'Clientes de la ruta',
        description:
          'La lista de clientes que casan con los filtros. Clic en una fila abre su perfil 360º (datos, pedidos, conversación, ubicación).',
        side: 'top',
      },
    },
    {
      element: '[data-tour="logistica-ampliar"]',
      popover: {
        title: 'Ampliar la tabla',
        description:
          'Abre la tabla grande en un modal: todo el scroll, todas las columnas y los filtros, sin que la pantalla se quede corta.',
        side: 'left',
      },
    },
  ],
}

/** Tour específico de Gestión. */
const TOUR_GESTION: DefinicionTour = {
  id: 'gestion',
  titulo: 'Guía de Gestión',
  pasos: [
    {
      element: '[data-tour="gestion-tabs"]',
      popover: {
        title: 'Las cuatro bandejas',
        description:
          'Gestión reúne todo lo que pide una acción humana. Cada pestaña es una bandeja distinta; te las muestro una a una.',
        side: 'bottom',
      },
    },
    {
      element: '[data-tour="gestion-semana"]',
      popover: {
        title: 'Esta semana',
        description:
          'A quién toca consultar ahora. Son los clientes del ciclo a los que hay que preguntar si quieren reparto esta semana.',
        side: 'bottom',
      },
    },
    {
      element: '[data-tour="gestion-seguimiento"]',
      popover: {
        title: 'Seguimiento',
        description:
          'Quién lleva tiempo sin pedir. La lista de clientes que se están «enfriando» para recuperarlos antes de perderlos.',
        side: 'bottom',
      },
    },
    {
      element: '[data-tour="gestion-incidencias"]',
      popover: {
        title: 'Incidencias',
        description:
          'Casos a resolver: pedidos especiales, peticiones fuera de catálogo y cualquier derivación del bot. Desde aquí saltas al chat del cliente.',
        side: 'bottom',
      },
    },
    {
      element: '[data-tour="gestion-rutas"]',
      popover: {
        title: 'Partes de reparto',
        description:
          'El resumen del día para el repartidor: paradas por ruta, con el PDF de albaranes combinado y los enlaces a Google Maps.',
        side: 'bottom',
      },
    },
  ],
}

/** Tour específico de Grandes Empresas. */
const TOUR_GE: DefinicionTour = {
  id: 'grandes-empresas',
  titulo: 'Guía de Grandes Empresas',
  pasos: [
    {
      element: '[data-tour="ge-buscar"]',
      popover: {
        title: 'Buscar GE',
        description:
          'Filtra las grandes empresas por nombre, CIF o población. Son clientes con reparto recurrente que no pasan por el ciclo semanal normal.',
        side: 'bottom',
      },
    },
    {
      element: '[data-tour="ge-lista"]',
      popover: {
        title: 'Tarjeta de cada GE',
        description:
          'El nombre abre su perfil 360º. El chip de cada tarjeta muestra su recurrencia actual (cada cuántos días se le reparte) o «sin fijar» si aún no está configurada.',
        side: 'top',
      },
    },
    {
      element: '[data-tour="ge-recurrencia"]',
      popover: {
        title: 'Configurar recurrencia',
        description:
          'Fija cada cuántos días se repite y el pedido fijo. Con eso, el sistema genera el albarán solo y lo coloca en el calendario, sin que tengas que agendarlo a mano cada vez.',
        side: 'top',
      },
    },
  ],
}

/** Tour específico de Agendamiento. */
const TOUR_AGENDAMIENTO: DefinicionTour = {
  id: 'agendamiento',
  titulo: 'Guía de Agendamiento',
  pasos: [
    {
      element: '[data-tour="agenda-modo"]',
      popover: {
        title: 'Formas de agendar',
        description:
          'Dos maneras de trabajar: «Tabla de clientes» (lo habitual: eliges clientes y preparas su pedido uno a uno) o «Importar Excel» (pegas una lista para cargar muchos de golpe).',
        side: 'bottom',
      },
    },
    {
      element: '#fecha-reparto, [aria-label="Fecha de reparto"]',
      popover: {
        title: 'Fecha de reparto',
        description:
          'El día para el que se crearán los albaranes. El pedido se emite a las 08:00 de esa fecha, que es la hora por la que filtra el reparto.',
        side: 'bottom',
      },
    },
    {
      element: '[data-tour="agenda-dia"]',
      popover: {
        title: 'Día de ruta',
        description:
          'Filtra la lista por el día de reparto de cada cliente (Lun–Vie). Cada día tiene su color para leerlo de un vistazo.',
        side: 'bottom',
      },
    },
    {
      element: '[data-tour="agenda-semana"]',
      popover: {
        title: 'Semana del ciclo',
        description:
          'Filtra por la semana del mes (S1–S4) que le toca a cada cliente. Así preparas solo a los que reparten esta semana.',
        side: 'bottom',
      },
    },
    {
      element: '[data-tour="agenda-buscar"]',
      popover: {
        title: 'Buscar cliente',
        description:
          'Localiza un cliente por nombre o NIF. Sugiere mientras escribes; al elegir uno, la tabla se queda filtrada por él.',
        side: 'bottom',
      },
    },
    {
      element: '[data-tour="agenda-tabla"]',
      popover: {
        title: 'Clientes del reparto',
        description:
          'La columna «Reparto» muestra el pedido que se enviará (el acordado si lo configuraste, o el último albarán). Marca clientes y pulsa «Configurar» en cada fila para ajustar su pedido, el día y la semana.',
        side: 'top',
      },
    },
  ],
}

/** Tour específico de Calendario. */
const TOUR_CALENDARIO: DefinicionTour = {
  id: 'calendario',
  titulo: 'Guía de Calendario',
  pasos: [
    {
      element: '[data-tour="cal-vista"]',
      popover: {
        title: 'Vista Mes o Semana',
        description:
          'Alterna entre el mes completo (visión general de los repartos) y la semana (el detalle de cada día con sus paradas).',
        side: 'bottom',
      },
    },
    {
      element: '[aria-label="Buscar cliente en el calendario"]',
      popover: {
        title: 'Buscar en el calendario',
        description:
          'Escribe un cliente y el calendario resalta los días en los que tiene reparto. Útil para ver de un vistazo su recurrencia.',
        side: 'bottom',
      },
    },
  ],
}

/** Tour específico de Conversaciones. */
const TOUR_CONVERSACIONES: DefinicionTour = {
  id: 'conversaciones',
  titulo: 'Guía de Conversaciones',
  pasos: [
    {
      element: '[data-tour="conv-buscar"]',
      popover: {
        title: 'Buscar conversación',
        description:
          'Localiza una conversación por nombre o teléfono del cliente. Busca en toda la bandeja, también en las conversaciones antiguas.',
        side: 'bottom',
      },
    },
    {
      element: '[data-tour="conv-filtros"]',
      popover: {
        title: 'Filtrar la bandeja',
        description:
          'Separa por tipo (Todas / Leads / Clientes) y por zona. Cada chip lleva su contador para ver cuántas hay de cada clase.',
        side: 'bottom',
      },
    },
    {
      element: '[data-tour="conv-lista"]',
      popover: {
        title: 'La bandeja',
        description:
          'Cada fila es una conversación: nombre, último mensaje, fecha y el ánimo detectado (positivo/neutro/negativo). Clic para abrirla.',
        side: 'right',
      },
    },
    {
      element: '[data-tour="conv-ventana"]',
      popover: {
        title: 'Ventana de 24 horas',
        description:
          'El chip dice si puedes escribir texto libre. WhatsApp solo deja responder dentro de las 24h tras el último mensaje del cliente; si está cerrada, hay que iniciar con una plantilla.',
        side: 'bottom',
      },
    },
    {
      element: '[data-tour="conv-compositor"]',
      popover: {
        title: 'Responder como humano',
        description:
          'Desde aquí escribes al cliente. Al enviar, el bot se pausa para ese contacto (no pisa tu conversación) hasta que lo reanudes.',
        side: 'top',
      },
    },
  ],
}

/**
 * Mapa ruta -> tour de pantalla. La ruta se compara por prefijo (startsWith)
 * para que sub-rutas (p.ej. /logistica?x=1) resuelvan su tour.
 */
export const TOURS_POR_RUTA: { prefijo: string; tour: DefinicionTour }[] = [
  { prefijo: '/logistica', tour: TOUR_LOGISTICA },
  { prefijo: '/gestion', tour: TOUR_GESTION },
  { prefijo: '/grandes-empresas', tour: TOUR_GE },
  { prefijo: '/agendamiento', tour: TOUR_AGENDAMIENTO },
  { prefijo: '/calendario', tour: TOUR_CALENDARIO },
  { prefijo: '/conversaciones', tour: TOUR_CONVERSACIONES },
]

/** Devuelve el tour de pantalla para una ruta, o null si no hay. */
export function tourDeRuta(ruta: string): DefinicionTour | null {
  return TOURS_POR_RUTA.find((t) => ruta.startsWith(t.prefijo))?.tour ?? null
}
